import React, {useEffect, useState} from "react";
import { RadioGroup, ReversedRadioButton } from "react-radio-buttons";
import { useForm } from "react-hook-form";
import StepHeader from "./stepParts/StepHeader";
import {
  currencyToNumber, numberToCurrency,
  rewriteRadioOptionsClasses,
  stepStates,
} from "../../services/helpers";
import StepSection from "./stepParts/StepSection";
import Input from "../core/Input";
import { gtm } from "../../services/gtm";
import { useSelector } from "react-redux";
import {getStep2HeaderSummary, getStepParams} from "../../services/stepHelpers";
import NextButton from "./stepParts/NextButton";
import {FormattedMessage, useIntl} from "react-intl";
import {assetsBaseUrl} from "../../services/calculationsApi";
import ReactTooltip from "react-tooltip";

function Step2({
    stepNumber,
    state,
    loading,
    openStep,
    onStepSubmit,
    shouldReset,
    setResetForms,
}) {
    const intl = useIntl();
    const [HasLoans, setHasLoans] = useState();
    const [stepHeaderSummary, setStepHeaderSummary] = useState();
    const [PrivateLeaseFull, setPrivateLeaseFull] = useState();
    const { HasPartner } = useSelector((state) => state.calculator);
    const [params] = useState(getStepParams());

    const getDefaultValues = (params) => {
      return {
        Loans: numberToCurrency(params?.Loans, true),
        Alimony: numberToCurrency(params?.Alimony, true),
        StudentDebtAmount: numberToCurrency(params?.StudentDebtAmount, true),
        PrivateLeaseAmount: numberToCurrency(params?.PrivateLeaseAmount, true),
      }
    }
    const { register, handleSubmit, formState, reset} = useForm({
        mode: "onChange",
        defaultValues: getDefaultValues(params),
    });

    const isFormValid = formState.isValid && HasLoans;

    useEffect(() => {
        setPrivateLeaseFull(params?.PrivateLeaseFull ? "1" : "0")
        setHasLoans(params?.HasLoans ? "1" : "0");
    }, [setHasLoans, params]);

    useEffect(() => {
        params &&
            setStepHeaderSummary(
                getStep2HeaderSummary(
                    params?.HasLoans ? "1" : "0",
                    params?.Loans,
                    params?.Alimony,
                    params?.StudentDebtAmount,
                    params?.PrivateLeaseAmount,
                    intl
                )
            );
    }, [setStepHeaderSummary, params, intl]);

    useEffect(() => {
        if (shouldReset) {
            reset( getDefaultValues({}));
            setStepHeaderSummary("");
            setResetForms(false);
        }
    }, [shouldReset, reset, setResetForms]);

    rewriteRadioOptionsClasses("financial-obligations", ["yes", "no"]);

    const onRadioChange = (value) => setHasLoans(value);

    const onSubmit = (data) => {
      //console.log('onSubmit', data);
        data.Loans = currencyToNumber(data.Loans, true);
        data.Alimony = currencyToNumber(data.Alimony, true);
        data.PrivateLeaseAmount = currencyToNumber(data.PrivateLeaseAmount, true);
        data.PrivateLeaseFull = PrivateLeaseFull === "1";
        data.StudentDebtAmount = currencyToNumber(data.StudentDebtAmount, true);
        data.HasLoans = !!parseInt(HasLoans);
        //data.StudentDebtMonthlyPayment = data.StudentDebtAmount;

        setStepHeaderSummary(
            getStep2HeaderSummary(
                HasLoans,
                data.Loans,
                data.Alimony,
                data.StudentDebtAmount,
                data.PrivateLeaseAmount,
                intl
            )
        );

        const tagManagerArgs = {
            gtmId: "GTM-T78CX5",
            dataLayer: {
                event: "gtm.calculators",
                eventCategory: "Calculator",
                eventAction: "Kredieten en leningen",
                eventLabel: !!parseInt(HasLoans)
                    ? "verplichtingen-ja"
                    : "verplichtingen-nee",
            },
        };

        gtm(tagManagerArgs);

        onStepSubmit(data, stepNumber);
    };

    return (
        <div
            className={`ct-step ${
                state === stepStates.inactive && "ct-step__inactive"
            }
                                 ${
                                     state === stepStates.filled &&
                                     "ct-step__filled"
                                 }
                                 ${
                                     state === stepStates.closed &&
                                     "ct-step__closed"
                                 }`}
        >
            <StepHeader
                title={intl.formatMessage({id: "step2.headerTitle", defaultMessage: "Kredieten en leningen"})}
                stepSummary={stepHeaderSummary}
                stepNumber={stepNumber}
                stepState={state}
                openStep={openStep}
                onClose={handleSubmit(onSubmit)}
            />

            <div
                className={`ct-step__body ${
                    state === stepStates.active ? "ct-step__body-opened" : null
                }`}
            >
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="ct-step__body-primary-question">
                      <FormattedMessage
                      id="step2.financialObligations"
                      defaultMessage="{salutation} maandelijkse financiële verplichtingen? *"
                      values={{
                        salutation: HasPartner ?
                        intl.formatMessage({ id: 'step2.financialObligations.hasPartner.yes', defaultMessage: 'Hebben jullie' }) :
                        intl.formatMessage({ id: 'step2.financialObligations.hasPartner.no', defaultMessage: 'Heb je' })
                      }}
                      />
                    </div>

                    <div className="ct-step__body-tip">
                      <FormattedMessage id="step2.financialObligations.tip" defaultMessage="Hieronder vallen o.a. een private lease auto, partner alimentatie, (studie)schulden of kredieten. Vul alleen in wat van toepassing is."/>
                    </div>

                    <RadioGroup
                        onChange={onRadioChange}
                        value={HasLoans}
                        horizontal
                        className="ct-step__radio-group financial-obligations"
                    >
                        <ReversedRadioButton
                            value="1"
                            rootColor="#000000"
                            pointColor="#000000"
                            iconInnerSize={18}
                            iconSize={18}
                        >
                          <FormattedMessage id="step2.financialObligations.answer.yes" defaultMessage="Ja"/>

                        </ReversedRadioButton>

                        <ReversedRadioButton
                            value="0"
                            rootColor="#000000"
                            pointColor="#000000"
                            iconInnerSize={18}
                            iconSize={18}
                        >
                          <FormattedMessage id="step2.financialObligations.answer.no" defaultMessage="Nee"/>

                        </ReversedRadioButton>
                    </RadioGroup>

                    {HasLoans === "1" ? (
                        <div className="ct-step__sections">
                            <StepSection title={intl.formatMessage({ id: 'step2.parts.loans.title', defaultMessage: 'Leningen'})}>
                                <div className="ct-step__body-tip">
                                  <FormattedMessage id="step2.parts.loans.question" defaultMessage="Hoeveel betaal je maandelijks aan de aflossing van je lening, inclusief rente?"/>
                                </div>

                                <Input
                                    placeholder="€ 200"
                                    register={register}
                                    type="currency"
                                    name="Loans"
                                    className="ct-step__section-input"
                                />
                            </StepSection>

                            <StepSection title={intl.formatMessage({ id: 'step2.parts.alimony.title', defaultMessage: 'Partneralimentatie'})}>
                              <div className="ct-step__body-tip">
                                <FormattedMessage id="step2.parts.alimony.question" defaultMessage="Hoeveel betaal je maandelijks aan partneralimentatie?"/>
                              </div>
                                <Input
                                    placeholder="€ 500"
                                    register={register}
                                    type="currency"
                                    name="Alimony"
                                    className="ct-step__section-input"
                                />
                            </StepSection>

                            <StepSection title={intl.formatMessage({ id: 'step2.parts.studentDebt.title', defaultMessage: 'Studieschuld'})}>
                                <div className="ct-step__body-tip">
                                  <div className="margin-bottom-5">
                                    <FormattedMessage id="step2.parts.studentDebt.question.title"
                                      defaultMessage="Hoeveel {salutation} maandelijks (gaan) aflossen op {salutation2} studieschuld?"
                                      values={{
                                        salutation: HasPartner ?
                                        intl.formatMessage({ id: 'step2.parts.studentDebt.question.title.salutation.hasPartner.yes', defaultMessage: 'moeten jullie' }) :
                                        intl.formatMessage({ id: 'step2.parts.studentDebt.question.title.salutation.hasPartner.no', defaultMessage: 'moet je' }),
                                        salutation2: HasPartner ?
                                        intl.formatMessage({ id: 'step2.parts.studentDebt.question.title.salutation2.hasPartner.yes', defaultMessage: 'de' }) :
                                        intl.formatMessage({ id: 'step2.parts.studentDebt.question.title.salutation2.hasPartner.no', defaultMessage: 'je' })
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <FormattedMessage id="step2.parts.studentDebt.question.description"
                                                      defaultMessage="Vul hier het wettelijke maandbedrag in. Niet het bedrag dat {salutation} op basis van {salutation2} huidige inkomen."
                                                      values={{
                                                        salutation: HasPartner ?
                                                        intl.formatMessage({
                                                          id: 'step2.parts.studentDebt.question.description.salutation.hasPartner.yes',
                                                          defaultMessage: 'betaald wordt'
                                                        }) :
                                                        intl.formatMessage({
                                                          id: 'step2.parts.studentDebt.question.description.salutation.hasPartner.no',
                                                          defaultMessage: 'je betaalt'
                                                        }),
                                                        salutation2: HasPartner ?
                                                        intl.formatMessage({
                                                          id: 'step2.parts.studentDebt.question.description.salutation2.hasPartner.yes',
                                                          defaultMessage: 'het'
                                                        }) :
                                                        intl.formatMessage({
                                                          id: 'step2.parts.studentDebt.question.description.salutation2.hasPartner.no',
                                                          defaultMessage: 'je'
                                                        })
                                                      }}
                                    />
                                    <img
                                    className="ct-step__tool-tip ct-step__tool-tip--align-middle"
                                    data-tip={intl.formatMessage({
                                      id: 'step2.parts.studentDebt.tip.tooltip',
                                      defaultMessage: 'Het wettelijk maandbedrag is het bedrag dat je moet betalen om de studieschuld binnen de looptijd af te betalen. Als je inkomen te laag is, wordt er o.b.v. je inkomen bekeken wat je naar draagkracht kunt betalen.'
                                    })}
                                    src={`${assetsBaseUrl}/assets/svg/info.svg`}
                                    alt=""
                                    />
                                    <ReactTooltip place={"right"} effect={"solid"}/>
                                  </div>
                                </div>

                              <Input
                              placeholder="€ 150"
                              register={register}
                              type="currency"
                              name="StudentDebtAmount"
                              className="ct-step__section-input"
                              />
                              <div className="ct-step__body-tip margin-bottom-20">
                                <FormattedMessage id={`step2.parts.studentDebt.tip`}
                                                  defaultMessage={`Weet je de maandelijkse aflossing niet? Dit kun je vinden op {linkInfo}. Als je nog niet aflost, doe dan op {linkCalc} de proefberekening om je maandlast in te schatten.`}
                                                  values={{
                                                    linkInfo: <a className={`ct-step__hypo-type-link`}
                                                                 href={`https://duo.nl/particulier/inloggen-op-mijn-duo.jsp`}
                                                                 target={`_blank`}>Mijn DUO</a>,
                                                    linkCalc: <a className={`ct-step__hypo-type-link`}
                                                                 href={`https://duo.nl/particulier/rekenhulp-studiefinanciering.jsp#/nl/terugbetalen/start`}
                                                                 target={`_blank`}>duo.nl</a>
                                                  }}
                                />

                              </div>

                            </StepSection>

                          <StepSection title={intl.formatMessage({
                            id: 'step2.parts.privateLease.title',
                            defaultMessage: 'Private Lease'
                          })}>
                            <div className="ct-step__body-tip ct-step__body-tip--flex">
                              <FormattedMessage id="step2.parts.privateLease.question"
                                                defaultMessage="Wat is het maandbedrag van je private lease auto?"/>
                            </div>

                            <Input
                            placeholder="€ 400"
                            register={register}
                            type="currency"
                            name="PrivateLeaseAmount"
                            className="ct-step__section-input"
                            />
                          </StepSection>
                        </div>
                    ) : null}

                    <NextButton
                        loading={loading}
                        isFormValid={isFormValid}
                        formState={formState}
                    ></NextButton>
                </form>
            </div>
        </div>
    );
}

export default Step2;
